import { eventChannel, EventChannel } from 'redux-saga';
import { HostedFields } from 'components/tranzila-hosted-fields/types';
import { logAndTrackError } from 'helpers/jstracking';
import { PaymentMethod } from 'store/state/app';
import { IMutationResponse } from 'store/state/mutationsResponse/selectors';
import { MutationType } from 'store/sagas/apiService/types';

export interface HostedFieldsEvent {
  isValid?: boolean;
  field?: string;
  type: string;
  [key: string]: any;
}

export interface HostedFieldsErrors {
  card_holder_id_number: boolean | null;
  credit_card_number: boolean | null;
  cvv: boolean | null;
  expiry: boolean | null;
}

export interface ChargeError {
  code: number;
  message: string;
  param: string;
}

export interface ChargeResponse {
  transaction_response: any;
  response_hash?: string;
  errors?: ChargeError[];
}

export interface InitHostedFieldsPaymentData {
  thtk: string;
  terminalName: string;
  amount: number;
  currencyCode: string;
  notifyUrl: string;
  productDescription: string;
}

const TRANZILA_TRANSACTION_TIMEOUT = 1000 * 30;
const TRANZILA_TIMEOUT_ERROR = 'TRANZILA_TIMEOUT_ERROR';
const TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR = 'TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR';

class TranzilaHostedFieldsService {
  private instance: HostedFields | null = null;
  private isReadyState: boolean = false;
  private readyListeners: Array<() => void> = [];

  public isReady(): boolean {
    return Boolean(this.instance);
  }

  public getInstance(): HostedFields | null {
    return this.instance;
  }

  // Subscribes to the Tranzila SDK 'ready' event, which signals all hosted-field iframes have finished rendering.
  public onReady(callback: () => void): () => void {
    if (this.isReadyState) {
      callback();
      return () => { /* noop, already invoked */ };
    }

    this.readyListeners.push(callback);

    return () => {
      this.readyListeners = this.readyListeners.filter((cb) => cb !== callback);
    };
  }

  private notifyReady(): void {
    this.isReadyState = true;
    const listeners = this.readyListeners;
    this.readyListeners = [];
    listeners.forEach((cb) => {
      try {
        cb();
      }
      catch (error) {
        logAndTrackError('HOSTED FIELDS: onReady listener threw', error);
      }
    });
  }

  public async waitForSdk(maxRetries: number = 10, retryDelay: number = 300): Promise<void> {
    let retries = 0;

    while (
      (
        typeof window === 'undefined'
        || !window.TzlaHostedFields
        || typeof window.TzlaHostedFields.create !== 'function'
      )
      && retries < maxRetries
    ) {
      retries += 1;
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }

    if (
      typeof window === 'undefined'
      || !window.TzlaHostedFields
      || typeof window.TzlaHostedFields.create !== 'function'
    ) {
      logAndTrackError('HOSTED FIELDS: Tranzila SDK is not available during init',
        {
          hasWindow: typeof window !== 'undefined',
          hasTzlaHostedFields: typeof window !== 'undefined' && !!window.TzlaHostedFields,
          hasCreate:
            typeof window !== 'undefined'
            && !!window.TzlaHostedFields
            && typeof window.TzlaHostedFields.create === 'function',
          retries,
        }
      );

      throw new Error('TRANZILA_SDK_NOT_AVAILABLE');
    }
  }

  public async init(handshakeData: InitHostedFieldsPaymentData | null, isGPay?: boolean): Promise<HostedFields> {
    await this.waitForSdk();

    return isGPay ? this.initGPayPayment(handshakeData) : this.initCardPayment(handshakeData);
  }

  public initCardPayment(handshakeData: InitHostedFieldsPaymentData | null): HostedFields {
    try {
      this.instance = window.TzlaHostedFields.create({
        sandbox: false,
        thtk: handshakeData ? handshakeData.thtk : null,
        fields: {
          credit_card_number: {
            selector: '#credit_card_num',
            placeholder: 'מספר כרטיס',
          },
          cvv: {
            selector: '#cvv',
            placeholder: 'CVV',
          },
          expiry: {
            selector: '#expiry',
            version: '1',
            placeholder: 'תוקף הכרטיס',
          },
          card_holder_id_number: {
            selector: '#card_holder_id_number',
            placeholder: 'תעודת זהות',
          },
        },
        styles: {
          input: {
            padding: '12px 16px',
            'border-radius': '16px',
            width: '100%',
            border: '1px solid #d3d3d3',
            height: '100%',
            direction: 'ltr',
            'text-align': 'right',
            'font-family': 'novemberHebrew, Muli, sans-serif',
          },
          'input:focus': {
            background: '#f8f8f8',
            border: '1px solid #303030',
            'box-shadow': '0px 0px 0px 2px #303030 inset',
          },
          '.hosted-fields-invalid:not(:focus)': {
            border: '1px solid #FF6658',
          },
        },
      });

      this.instance.onEvent('ready', () => {
        this.notifyReady();
      });

      return this.instance;
    }
    catch (error) {
      logAndTrackError('HOSTED FIELDS: Failed to create Tranzila hosted fields instance', error);

      this.instance = null;
      throw error;
    }
  }

  public async initGPayPayment(handshakeData: InitHostedFieldsPaymentData | null): Promise<HostedFields> {
    await this.waitForSdk();

    try {
      this.instance = window.TzlaHostedFields.create({
        sandbox: false,
        thtk: handshakeData ? handshakeData.thtk : null,
        fields: {
          credit_card_number: {
            selector: '#credit_card_num',
            placeholder: 'GPAY',
          },
        },
      });

      return this.instance;
    }
    catch (error) {
      logAndTrackError('HOSTED FIELDS: Failed to create Tranzila hosted fields instance', error);

      this.instance = null;
      throw error;
    }
  }

  public initGPay(thtk: string) {
    try {
      this.instance = window.TzlaHostedFields.create({
        sandbox: false,
        thtk,
        fields: {
          credit_card_number: {
            selector: '#credit_card_num',
            placeholder: 'GPAY',
          },
        },
      });

      return this.instance;
    }
    catch (error) {
      logAndTrackError('GPAY init last test', error);
      return this.instance;
    }
  }

  public destroy(): void {
    this.instance = null;
    this.isReadyState = false;
    this.readyListeners = [];
  }

  public createChannel(): EventChannel<HostedFieldsEvent> {
    if (!this.instance) {
      throw new Error('TRANZILA_INSTANCE_NOT_INITIALIZED');
    }

    return eventChannel((emit) => {
      const handler = (event: HostedFieldsEvent) => {
        emit(event);
      };

      this.instance.onEvent('validityChange', handler);
      this.instance.onEvent('googleIsEnable', handler);

      return () => {
        // offEvent/removeListener - if supported by Tranzila
      };
    });
  }

  private getInitDataOrThrow(response: IMutationResponse<MutationType.InitHostedFieldsPayment> | null): InitHostedFieldsPaymentData {
    const initData =
      response
      && response.data
      && response.data.initHostedFieldsPayment
        ? response.data.initHostedFieldsPayment
        : null;

    if (!initData) {
      logAndTrackError('HOSTED FIELDS: Init hosted fields payment data is missing before charge', response);

      throw new Error(TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR);
    }

    return initData;
  }

  private ensureInstanceOrThrow(context: string): HostedFields {
    if (!this.instance) {
      logAndTrackError(`HOSTED FIELDS: TRANZILA instance is missing before ${context}`);

      throw new Error(TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR);
    }

    return this.instance;
  }

  public getPaymentFn(response: IMutationResponse<MutationType.InitHostedFieldsPayment>, method: PaymentMethod): () => Promise<any> {
    switch (method) {
      case PaymentMethod.CARD:
        return () => this.chargeCard(response);
      case PaymentMethod.GOOGLE_PAY:
        return () => this.chargeGooglePay(response);
      default:
        return null;
    }
  }

  public chargeCard(initPaymentResponse: IMutationResponse<MutationType.InitHostedFieldsPayment>): Promise<any> {
    return new Promise((resolve, reject) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout>;

      const safeResolve = (value: any) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeoutId);
        resolve(value);
      };

      const safeReject = (error: Error) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeoutId);
        reject(error);
      };

      try {
        const tranzila = this.ensureInstanceOrThrow('credit card charge');
        const initData = this.getInitDataOrThrow(initPaymentResponse);

        const requestPayload = {
          terminal_name: initData.terminalName,
          amount: initData.amount,
          currency_code: initData.currencyCode,
          response_language: 'hebrew',
          thtk: initData.thtk,
          notify_url: initData.notifyUrl,
          tokenize: true,
          pdesc: initData.productDescription,
        };

        timeoutId = setTimeout(() => {
          logAndTrackError('HOSTED FIELDS: Credit card charge request timed out', { paymentRequest: requestPayload });

          safeReject(new Error(TRANZILA_TIMEOUT_ERROR));
        }, TRANZILA_TRANSACTION_TIMEOUT);

        tranzila.charge(requestPayload, (_: unknown, response: ChargeResponse) => {
          if (
            response
            && !response.transaction_response
            && response.errors
            && response.errors.length
          ) {
            logAndTrackError('HOSTED FIELDS: Credit card charge returned errors without transaction response', response);

            safeReject(new Error(TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR));
            return;
          }

          safeResolve(response ? response.transaction_response : undefined);
        });
      }
      catch (error) {
        logAndTrackError('HOSTED FIELDS: Credit card charge threw an exception', error);

        safeReject(
          error instanceof Error
            ? error
            : new Error(TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR)
        );
      }
    });
  }

  public chargeGooglePay(initPaymentResponse: IMutationResponse<MutationType.InitHostedFieldsPayment>): Promise<any> {
    return new Promise((resolve, reject) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout>;

      const safeResolve = (value: any) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeoutId);
        resolve(value);
      };

      const safeReject = (error: Error) => {
        if (settled) {
          return;
        }

        settled = true;
        clearTimeout(timeoutId);
        reject(error);
      };

      try {
        const tranzila = this.ensureInstanceOrThrow('Google Pay charge');
        const initData = this.getInitDataOrThrow(initPaymentResponse);

        const requestPayload = {
          terminal_name: initData.terminalName,
          amount: initData.amount ? initData.amount.toString() : undefined,
          currency_code: initData.currencyCode,
          thtk: initData.thtk,
          response_language: 'english',
          tran_mode: 'A',
          notify_url: initData.notifyUrl,
          pdesc: initData.productDescription,
        };

        timeoutId = setTimeout(() => {
          logAndTrackError('HOSTED FIELDS: Google Pay charge request timed out', { paymentRequest: requestPayload });

          safeReject(new Error(TRANZILA_TIMEOUT_ERROR));
        }, TRANZILA_TRANSACTION_TIMEOUT);

        tranzila.chargeGpay(requestPayload, (_: unknown, response: ChargeResponse) => {
          if (
            response
            && !response.transaction_response
            && response.errors
            && response.errors.length
          ) {
            logAndTrackError('HOSTED FIELDS: Google Pay charge returned errors without transaction response', response);

            safeReject(new Error(TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR));
            return;
          }

          safeResolve(response ? response.transaction_response : undefined);
        });
      }
      catch (error) {
        logAndTrackError('HOSTED FIELDS: Google Pay charge threw an exception', error);

        safeReject(
          error instanceof Error
            ? error
            : new Error(TRANZILA_PAYMENT_NOT_SUBMITTED_ERROR)
        );
      }
    });
  }

  public chargeGooglePayLastCheck(conf: InitHostedFieldsPaymentData & { lang?: string, tranMode?: string }) {
    try {
      const tranzila = this.ensureInstanceOrThrow('Google Pay charge');

      const requestPayload = {
        terminal_name: conf.terminalName,
        amount: conf.amount,
        currency_code: conf.currencyCode,
        thtk: conf.thtk,
        response_language: conf.lang,
        tran_mode: conf.tranMode,
        notify_url: conf.notifyUrl,
        pdesc: conf.productDescription,
      };

      tranzila.chargeGpay(requestPayload, (_: unknown, response: ChargeResponse) => {
        if (
          response
          && !response.transaction_response
          && response.errors
          && response.errors.length
        ) {
          return;
        }

      });
    }
    catch (error) {
      logAndTrackError('GPAY last test', error);
    }
  }
}

export const tranzilaHostedFieldsService = new TranzilaHostedFieldsService();
